import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtRM } from "@/lib/constants";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { BarChart3, DollarSign, TrendingUp, Megaphone } from "lucide-react";

const COLORS = ["#059669", "#0284C7", "#F59E0B", "#7C3AED", "#E11D48", "#10B981"];

export default function Analytics() {
  const [range, setRange] = useState("7d");
  const { data } = useQuery({ queryKey: ["analytics"], queryFn: () => api.get("/analytics").then((r) => r.data) });
  if (!data) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;

  const roi = data.total_cost > 0 ? Math.round(((data.campaign_revenue - data.total_cost) / data.total_cost) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="h-6 w-6 text-primary" /> Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Ukur perbualan, leads, sales, conversion & ROI dari WhatsApp.</p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger data-testid="analytics-timefilter-select" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 hari</SelectItem><SelectItem value="30d">30 hari</SelectItem><SelectItem value="90d">90 hari</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="Total Revenue" value={fmtRM(data.total_revenue)} testid="analytics-revenue" />
        <KPI icon={Megaphone} label="Broadcast Revenue" value={fmtRM(data.campaign_revenue)} testid="analytics-campaign-revenue" />
        <KPI icon={TrendingUp} label="Broadcast ROI" value={`${roi}%`} testid="analytics-roi" />
        <KPI icon={BarChart3} label="Kempen" value={data.campaigns.length} testid="analytics-campaign-count" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display font-semibold mb-4">Aktiviti Mingguan</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Line type="monotone" dataKey="conversations" stroke="#059669" strokeWidth={2.5} dot={false} name="Perbualan" />
              <Line type="monotone" dataKey="leads" stroke="#0284C7" strokeWidth={2.5} dot={false} name="Leads" />
              <Line type="monotone" dataKey="sales" stroke="#F59E0B" strokeWidth={2.5} dot={false} name="Sales" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display font-semibold mb-4">Sumber Lead</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                {data.sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display font-semibold mb-4">Prestasi Kempen Broadcast</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.campaigns.filter((c) => c.status === "SENT")}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
            <Bar dataKey="replies" fill="#0284C7" radius={[6, 6, 0, 0]} name="Replies" />
            <Bar dataKey="leads" fill="#059669" radius={[6, 6, 0, 0]} name="Leads" />
            <Bar dataKey="sales" fill="#F59E0B" radius={[6, 6, 0, 0]} name="Sales" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="h-10 w-10 rounded-xl bg-accent text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
    <p className="mt-3 text-2xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);
